// Crypto-wallet endpoint functions.
//
// Endpoints take camelCase view-model inputs and return camelCase view-model
// outputs. Snake_case lives ONLY between the network and the parser.

import { adminApi } from '@core/api/httpClient'
import type {
  CryptoWalletAccountDto,
  CryptoWalletsResponseDto,
  TrustlineDto,
  WalletManagementDetailDto,
  WalletManagementListResponseDto,
} from './cryptoWalletContracts'
import type {
  CryptoWalletAccount,
  CryptoWalletsResponse,
  CustodyWalletApprovalInput,
  GetCryptoWalletsParams,
  GetWalletManagementParams,
  Trustline,
  TrustlineActionInput,
  WalletManagementDetail,
  WalletManagementListResponse,
} from '../types/cryptoWalletTypes'
import {
  parseCryptoWalletAccount,
  parseCryptoWalletsResponse,
  parseTrustline,
  parseWalletManagementDetail,
  parseWalletManagementListResponse,
} from './cryptoWalletParser'
import {
  APPROVE_CUSTODY_WALLET,
  CRYPTO_WALLETS,
  CUSTODY_WALLET,
  TRUSTLINES,
  TRUSTLINE_STATUS_CHECK,
  WALLET_MANAGEMENT_INVESTORS,
} from './cryptoWalletsEndpointPaths'

// admin-service wraps every response as `ApiResponse<T>`, but a few routes are
// read through proxies that have already unwrapped it. Accepting both shapes
// here keeps that difference out of every endpoint function below.
function unwrap<T>(body: { data?: T } | T): T {
  const envelope = (body as { data?: T })?.data
  return envelope ?? (body as T)
}

export async function getCryptoWallets(
  params: GetCryptoWalletsParams = {},
): Promise<CryptoWalletsResponse> {
  const queryParams: Record<string, string> = {}
  if (params.page) queryParams.page = params.page.toString()
  if (params.limit) queryParams.limit = params.limit.toString()
  if (params.search) queryParams.search = params.search
  if (params.status) queryParams.status = params.status
  if (params.trustlineStatus) queryParams.trustline_status = params.trustlineStatus
  if (params.asset) queryParams.asset = params.asset

  const { data } = await adminApi.get<
    { data?: CryptoWalletsResponseDto } | CryptoWalletsResponseDto
  >(CRYPTO_WALLETS, { params: queryParams })
  const envelope =
    (data as { data?: CryptoWalletsResponseDto })?.data ?? (data as CryptoWalletsResponseDto)
  return parseCryptoWalletsResponse(envelope ?? { rows: [], rowsCount: 0 })
}

// ── Wallet management ────────────────────────────────────────────────────────

export async function getWalletManagementList(
  params: GetWalletManagementParams = {},
): Promise<WalletManagementListResponse> {
  const queryParams: Record<string, string> = {}
  if (params.page) queryParams.page = params.page.toString()
  if (params.limit) queryParams.limit = params.limit.toString()
  if (params.search) queryParams.search = params.search
  if (params.walletStatus) queryParams.walletStatus = params.walletStatus
  if (params.awaitingOptInOnly) queryParams.awaitingOptInOnly = 'true'
  if (params.trustlineStatus) queryParams.trustlineStatus = params.trustlineStatus

  const { data } = await adminApi.get<
    { data?: WalletManagementListResponseDto } | WalletManagementListResponseDto
  >(WALLET_MANAGEMENT_INVESTORS, { params: queryParams })
  const envelope = unwrap<WalletManagementListResponseDto>(data)
  return parseWalletManagementListResponse(
    envelope ?? { data: [], meta: { total: 0, page: 1, limit: 0, totalPages: 0 } },
  )
}

export async function getWalletManagementDetail(
  investorId: string,
): Promise<WalletManagementDetail> {
  const { data } = await adminApi.get<
    { data?: WalletManagementDetailDto } | WalletManagementDetailDto
  >(`${WALLET_MANAGEMENT_INVESTORS}/${investorId}`)
  return parseWalletManagementDetail(unwrap<WalletManagementDetailDto>(data))
}

export async function createCustodyWallet(investorId: string): Promise<CryptoWalletAccount> {
  const { data } = await adminApi.post<{ data?: CryptoWalletAccountDto } | CryptoWalletAccountDto>(
    CUSTODY_WALLET(investorId),
    {},
  )
  return parseCryptoWalletAccount(unwrap<CryptoWalletAccountDto>(data))
}

export async function approveCustodyWallet(
  input: CustodyWalletApprovalInput,
): Promise<CryptoWalletAccount> {
  const { data } = await adminApi.post<{ data?: CryptoWalletAccountDto } | CryptoWalletAccountDto>(
    APPROVE_CUSTODY_WALLET(input.investorId, input.walletId),
    {},
  )
  return parseCryptoWalletAccount(unwrap<CryptoWalletAccountDto>(data))
}

export async function setupTrustline(input: TrustlineActionInput): Promise<Trustline> {
  const { data } = await adminApi.post<{ data?: TrustlineDto } | TrustlineDto>(TRUSTLINES, input)
  return parseTrustline(unwrap<TrustlineDto>(data))
}

export async function checkTrustlineStatus(input: TrustlineActionInput): Promise<Trustline> {
  const { data } = await adminApi.post<{ data?: TrustlineDto } | TrustlineDto>(
    TRUSTLINE_STATUS_CHECK,
    input,
  )
  return parseTrustline(unwrap<TrustlineDto>(data))
}
